export enum SolarMonitoringProvider {
  Enphase = 'enphase',
  SolarEdge = 'solaredge',
}

export enum SolarMonitoringConnectionStatus {
  Active = 'active',
  Expired = 'expired',
  Revoked = 'revoked',
  Error = 'error',
}

export enum SolarMonitoringResult {
  NotLinked = 'not_linked',
  ConnectivityOnly = 'connectivity_only',
  BriefProduction = 'brief_production',
  ThreeDayProduction = 'three_day_production',
  Stale = 'stale',
  Error = 'error',
}

export interface DailyProductionReading {
  date: string
  productionWh: number
  observedAt: Date
}

export interface OwnerMonitoringEquipmentItem {
  kind: string
  manufacturer?: string
  model?: string
  name?: string
  count: number
}

export interface OwnerMonitoringSystemSummary {
  currentPowerW?: number
  energyLifetimeWh?: number
  energyTodayWh?: number
  lastProducedAt?: Date
  lastReportedAt?: Date
  moduleCount?: number
  systemSizeW?: number
  status?: string
  source?: string
  summaryDate?: string
  batteryCapacityWh?: number
}

export const toUtcDateString = (date: Date): string => date.toISOString().slice(0, 10)

export const addUtcDays = (dateString: string, days: number): string => {
  const parsed = new Date(`${dateString}T00:00:00.000Z`)
  parsed.setUTCDate(parsed.getUTCDate() + days)
  return toUtcDateString(parsed)
}

export const getSolarMonitoringDateWindow = (
  days: number,
  endDate: Date = new Date(),
): { startDate: string, endDate: string } => {
  const normalizedEndDate = toUtcDateString(endDate)
  return {
    startDate: addUtcDays(normalizedEndDate, -Math.max(1, Math.floor(days))),
    endDate: normalizedEndDate,
  }
}

export const mergeDailyProductionReadings = (
  existing: DailyProductionReading[],
  incoming: DailyProductionReading[],
): DailyProductionReading[] => {
  const readingsByDate = new Map<string, DailyProductionReading>()
  for (const reading of existing) {
    readingsByDate.set(reading.date, reading)
  }
  for (const reading of incoming) {
    readingsByDate.set(reading.date, reading)
  }
  return Array.from(readingsByDate.values()).sort((left, right) => (
    left.date.localeCompare(right.date)
  ))
}

export interface SolarMonitoringEvidence {
  result: SolarMonitoringResult
  hasConnectivity: boolean
  qualifyingDates: string[]
  lastObservedAt?: Date
  totalProductionWh?: number
  errorCategory?: string
}

export interface SolarMonitoringDetails {
  monitoringSiteId: string
  provider: SolarMonitoringProvider
  externalSystemId: string
  evidence?: SolarMonitoringEvidence
}

export interface SolarMonitoringCheckPolicy {
  nominalProductionWh: number
  maxDataAgeHours: number
  requiredProductionDays: number
}

export const DEFAULT_SOLAR_MONITORING_CHECK_POLICY: SolarMonitoringCheckPolicy = {
  nominalProductionWh: 1,
  maxDataAgeHours: 48,
  requiredProductionDays: 3,
}

const toUtcDate = (date: string): Date => {
  const parsed = new Date(`${date}T00:00:00.000Z`)
  return parsed
}

const getConsecutiveDates = (dates: string[]): string[] => {
  const sortedDates = Array.from(new Set(dates)).sort()
  let longestRun: string[] = []
  let currentRun: string[] = []

  for (const date of sortedDates) {
    const previousDate = currentRun[currentRun.length - 1]
    if (!previousDate) {
      currentRun = [date]
    } else {
      const daysBetween = (
        toUtcDate(date).getTime() - toUtcDate(previousDate).getTime()
      ) / (24 * 60 * 60 * 1000)

      currentRun = daysBetween === 1 ? [...currentRun, date] : [date]
    }

    if (currentRun.length > longestRun.length) {
      longestRun = currentRun
    }
  }

  return longestRun
}

export const classifySolarMonitoringEvidence = (
  readings: DailyProductionReading[],
  policy: SolarMonitoringCheckPolicy = DEFAULT_SOLAR_MONITORING_CHECK_POLICY,
  checkedAt: Date = new Date(),
  hasConnectivity: boolean = readings.length > 0,
): SolarMonitoringEvidence => {
  const validReadings = readings.filter(reading => (
    reading.date &&
    Number.isFinite(reading.productionWh) &&
    reading.productionWh >= 0 &&
    reading.observedAt instanceof Date &&
    !Number.isNaN(reading.observedAt.getTime())
  ))

  const latestObservedAt = validReadings.reduce<Date | undefined>((latest, reading) => (
    !latest || reading.observedAt > latest ? reading.observedAt : latest
  ), undefined)

  if (!latestObservedAt) {
    return {
      result: hasConnectivity
        ? SolarMonitoringResult.ConnectivityOnly
        : SolarMonitoringResult.Error,
      hasConnectivity,
      qualifyingDates: [],
      errorCategory: hasConnectivity ? undefined : 'no_readings',
    }
  }

  const ageHours = (checkedAt.getTime() - latestObservedAt.getTime()) / (60 * 60 * 1000)
  if (ageHours > policy.maxDataAgeHours) {
    return {
      result: SolarMonitoringResult.Stale,
      hasConnectivity: true,
      qualifyingDates: [],
      lastObservedAt: latestObservedAt,
      totalProductionWh: validReadings.reduce((total, reading) => total + reading.productionWh, 0),
    }
  }

  const qualifyingDates = validReadings
    .filter(reading => reading.productionWh >= policy.nominalProductionWh)
    .map(reading => reading.date)
  const consecutiveDates = getConsecutiveDates(qualifyingDates)
  const totalProductionWh = validReadings.reduce((total, reading) => total + reading.productionWh, 0)
  const hasProduction = validReadings.some(reading => reading.productionWh > 0)

  return {
    result: consecutiveDates.length >= policy.requiredProductionDays
      ? SolarMonitoringResult.ThreeDayProduction
      : hasProduction
        ? SolarMonitoringResult.BriefProduction
        : SolarMonitoringResult.ConnectivityOnly,
    hasConnectivity: true,
    qualifyingDates: consecutiveDates,
    lastObservedAt: latestObservedAt,
    totalProductionWh,
  }
}

export const SolarMonitoringCheckKind = {
  Connectivity: 'connectivity',
  Production: 'production',
} as const

export type SolarMonitoringCheckKind =
  typeof SolarMonitoringCheckKind[keyof typeof SolarMonitoringCheckKind]

const CONNECTIVITY_VERIFIED_RESULTS: SolarMonitoringResult[] = [
  SolarMonitoringResult.ConnectivityOnly,
  SolarMonitoringResult.BriefProduction,
  SolarMonitoringResult.ThreeDayProduction,
  SolarMonitoringResult.Stale,
]

export const hasInverterAssignment = (
  provider?: SolarMonitoringProvider,
  externalSystemId?: string,
): boolean => Boolean(provider && externalSystemId?.trim())

export const hasVerifiedConnectivity = (evidence?: SolarMonitoringEvidence): boolean => (
  Boolean(evidence?.hasConnectivity) ||
  Boolean(evidence?.result && CONNECTIVITY_VERIFIED_RESULTS.includes(evidence.result))
)

export const hasThreeDayProduction = (result?: SolarMonitoringResult): boolean => (
  result === SolarMonitoringResult.ThreeDayProduction
)

export const isEligibleForEarlyFunding = (result?: SolarMonitoringResult): boolean => (
  Boolean(result && CONNECTIVITY_VERIFIED_RESULTS.includes(result))
)

export const isSolarMonitoringCheckComplete = (
  kind: SolarMonitoringCheckKind,
  evidence?: SolarMonitoringEvidence,
): boolean => (
  kind === SolarMonitoringCheckKind.Production
    ? hasThreeDayProduction(evidence?.result)
    : hasVerifiedConnectivity(evidence)
)

export const getSolarMonitoringStatusLabel = (
  kind: SolarMonitoringCheckKind,
  provider?: SolarMonitoringProvider,
  externalSystemId?: string,
  evidence?: SolarMonitoringEvidence,
): string => {
  const assigned = hasInverterAssignment(provider, externalSystemId)

  if (kind === SolarMonitoringCheckKind.Production) {
    if (evidence?.result === SolarMonitoringResult.ThreeDayProduction) {
      return 'Three consecutive qualifying production days detected'
    }
    if (evidence?.result === SolarMonitoringResult.BriefProduction) {
      return 'Brief production detected; three consecutive days not yet confirmed'
    }
    if (evidence?.result === SolarMonitoringResult.ConnectivityOnly) {
      return 'Connectivity detected; no qualifying production'
    }
    if (evidence?.result === SolarMonitoringResult.Stale) {
      return 'Provider data is stale'
    }
    if (evidence?.result === SolarMonitoringResult.Error) {
      return 'Unable to verify production data'
    }
    if (!assigned) {
      return 'Not configured'
    }
    return 'Unable to verify connectivity'
  }

  if (!assigned) {
    return 'Not configured'
  }
  if (!hasVerifiedConnectivity(evidence)) {
    return 'Unable to verify connectivity'
  }
  return 'Connectivity detected'
}

export const getOwnerMonitoringEvidenceLabel = (result?: SolarMonitoringResult): string => {
  if (result === SolarMonitoringResult.ThreeDayProduction) {
    return 'Full production verified'
  }
  if (result === SolarMonitoringResult.BriefProduction) {
    return 'Initial production detected'
  }
  if (result === SolarMonitoringResult.ConnectivityOnly) {
    return 'Connectivity only'
  }
  if (result === SolarMonitoringResult.Stale) {
    return 'Stale data'
  }
  if (result === SolarMonitoringResult.Error) {
    return 'Unable to verify'
  }
  return 'Not checked'
}

export const getSeventyThirtyAmounts = (loanAmount: number): {
  earlyAmount: number
  finalAmount: number
} => {
  const totalCents = Math.max(0, Math.round((Number.isFinite(loanAmount) ? loanAmount : 0) * 100))
  const earlyCents = Math.round(totalCents * 0.7)

  return {
    earlyAmount: earlyCents / 100,
    finalAmount: (totalCents - earlyCents) / 100,
  }
}
